import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import { IdentityService } from '../services/identity.service';

/** The error code of a 403 refused by the area permissions (`AreaPermissionDeniedResponse`). Kept
 *  here, not in extranet.model.ts: this interceptor is in the initial bundle, and a runtime import
 *  of the model would bring the whole model's runtime code there with it. */
export const AREA_PERMISSION_DENIED = 'AREA_PERMISSION_DENIED';

const READ_METHODS = new Set(['GET', 'HEAD']);

/** How long the "read-only" toast stays (ms), as the "no access" one of the area guard. */
export const AREA_DENIED_TOAST_MS = 4000;

/**
 * Page-area refusals (Feature 4123): the BE answers 403 `{ error: AREA_PERMISSION_DENIED, message,
 * area, required, granted }` to a request on a NONE area and to a write on a READ area. Registered
 * after `authInterceptor` and `viewAsInterceptor` in app.config.ts.
 *
 * - A refused write (any method but GET/HEAD) always says so, whatever the screen does with the
 *   error, in a `snack-error` toast chosen by the body's `granted`: READ → "Your profile can only
 *   view this page." (`profiles.readOnlyWrite`, also for a body without `granted`, as before);
 *   NONE → "You don't have access to this page. Ask your group Owner." (`profiles.noAccess`). The
 *   screens add no snackbar of their own on such a refusal (`isExplainedWriteRefusal`), which
 *   would replace this one. A refused read shows nothing here — the screen decides (a home widget
 *   hides itself, a page shows its error state).
 * - Either way the member's profiles or the admin matrix may have changed since GET /me last
 *   answered, so the identity is re-resolved (`IdentityService.refreshPermissions`, at most once
 *   per 10 s) and the menu and the pages follow.
 * - The error always goes on to the caller unchanged; any other error passes untouched.
 */
export const areaPermissionInterceptor: HttpInterceptorFn = (req, next) => {
  // Resolved only on a refusal: IdentityService itself calls the BE through HttpClient, and the
  // translations are loaded through it too — nothing is built for the requests that pass.
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 403 && err.error?.error === AREA_PERMISSION_DENIED) {
        if (!READ_METHODS.has(req.method.toUpperCase())) {
          const key = err.error.granted === 'NONE' ? 'profiles.noAccess' : 'profiles.readOnlyWrite';
          const text = injector.get(TranslateService).instant(key);
          injector.get(MatSnackBar).open(text, undefined, { duration: AREA_DENIED_TOAST_MS, panelClass: 'snack-error' });
        }
        injector.get(IdentityService).refreshPermissions();
      }
      return throwError(() => err);
    }),
  );
};
